import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  LayoutDashboard,
  ClipboardList,
  PlusCircle,
  Upload,
  Users,
  LogOut,
  Menu,
  X,
  Factory,
  Activity,
  Building2,
  Bell,
  Check,
  AtSign,
  Package,
  FileText,
  ShoppingCart,
  Beaker,
  BarChart3,
  Boxes
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map nav items to permission IDs
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard' },
  { path: '/tickets', label: 'All Tickets', icon: ClipboardList, permission: 'tickets' },
  { path: '/tickets/new', label: 'New Entry', icon: PlusCircle, permission: 'new_entry' },
  { path: '/raw-materials', label: 'Raw Materials', icon: Package, permission: 'raw_materials' },
  { path: '/supplier-samples', label: 'Supplier Samples', icon: Beaker, permission: 'supplier_samples' },
  { path: '/sales', label: 'Sales', icon: ShoppingCart, permission: 'raw_materials' },
  { path: '/invoices', label: 'Invoice Upload', icon: FileText, permission: 'raw_materials' },
  { path: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports' },
  { path: '/import', label: 'Batch Import', icon: Upload, permission: 'batch_import' },
  { path: '/products', label: 'Products', icon: Boxes, permission: 'product_management' },
  { path: '/users', label: 'User Management', icon: Users, permission: 'user_management' },
  { path: '/companies', label: 'Companies', icon: Building2, permission: 'company_management' },
  { path: '/activity', label: 'Activity Log', icon: Activity, permission: 'activity_log' },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { companies, selectedCompany, selectCompany } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/unread-count`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadNotifications(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Fetch full notifications list
  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=10`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include'
      });
      setUnreadNotifications(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  // Handle notification bell click
  const handleNotificationClick = () => {
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleCompanyChange = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      selectCompany(company);
    }
  };

  const filteredNavItems = navItems.filter(item => {
    const userPermissions = user?.effective_permissions || [];
    return userPermissions.includes(item.permission);
  });

  const getRoleDisplay = (role) => {
    const roleMap = {
      production: 'Production Team',
      lab: 'Lab Team',
      accounts: 'Accounts Team',
      manager: 'Manager'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${isMobile && !sidebarOpen ? 'sidebar-collapsed' : ''} ${isMobile && sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Factory className="w-6 h-6 text-white" />
            </div>
            <span>FactoryOps</span>
          </div>
          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={handleNotificationClick}
              className="relative p-2 hover:bg-slate-100 rounded-md transition-colors"
              data-testid="notification-bell"
              title="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
            
            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
                  {unreadNotifications > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      Mark all read
                    </button>
                  )}
                </div>
                
                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                      <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif.notification_id}
                        className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          !notif.read ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <AtSign className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900">
                              <span className="font-medium">{notif.sender_name}</span>
                              {' mentioned you'}
                            </p>
                            <p className="text-xs text-slate-500 truncate mt-0.5">
                              {notif.content_preview}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {formatTimeAgo(notif.created_at)}
                            </p>
                          </div>
                          {!notif.read && (
                            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2"></div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Company Selector */}
        <div className="px-4 py-3 border-b border-slate-200">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            <Building2 className="w-3 h-3 inline mr-1" />
            Company
          </label>
          <Select value={selectedCompany?.id || ''} onValueChange={handleCompanyChange}>
            <SelectTrigger className="h-10 bg-slate-50" data-testid="company-selector">
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="sidebar-nav">
          {filteredNavItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              data-testid={`nav-${item.path.replace(/\//g, '-')}`}
            >
              <item.icon />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} />
              ) : (
                <span className="text-slate-500 text-sm font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-role">{getRoleDisplay(user?.role)}</div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-slate-100 rounded-md transition-colors"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Mobile Header */}
        {isMobile && (
          <div className="page-header flex-col gap-2">
            <div className="flex items-center justify-between w-full">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-slate-100 rounded-md"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <Factory className="w-6 h-6 text-slate-900" />
                <span className="font-bold text-slate-900">FactoryOps</span>
              </div>
              <div className="w-10" />
            </div>
            {/* Mobile Company Selector */}
            <div className="w-full">
              <Select value={selectedCompany?.id || ''} onValueChange={handleCompanyChange}>
                <SelectTrigger className="h-10 bg-slate-50" data-testid="mobile-company-selector">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {children}
      </main>

      {/* Mobile Bottom Nav */}
      {isMobile && (
        <nav className="mobile-nav flex items-center justify-around px-2">
          {filteredNavItems.slice(0, 4).map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors ${
                location.pathname === item.path 
                  ? 'text-slate-900 bg-slate-100' 
                  : 'text-slate-500'
              }`}
              data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
            >
              <item.icon className="w-6 h-6" />
              <span className="text-xs mt-1 font-medium">{item.label.split(' ')[0]}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
};
